/**
 * Client-only UI state. Anything the server owns lives in TanStack Query —
 * this store holds what the *window* knows: which pane is showing what, what
 * the user has open, and the preferences that survive a reload.
 *
 * Appearance preferences live here rather than in a component because they are
 * applied by writing CSS custom properties onto <html>: the store is the only
 * thing that exists before the first render and outlives every unmount.
 */
import { create } from 'zustand';
import type { AddressInput, ThreadCategory, ThreadViewName } from './api';

export type Density = 'compact' | 'cozy' | 'comfortable';
export type Theme = 'light' | 'dark' | 'system';
/** One-line rows are the default, as in Superhuman; two lines is the opt-in. */
export type RowLines = 1 | 2;
export type Measure = 'narrow' | 'default' | 'wide' | 'full';
export type FontScale = 90 | 100 | 110;
/**
 * Where the conversation opens. `full` is Superhuman's behaviour and the
 * default: the reading view replaces the list and Esc goes back. The split
 * layouts keep the list on screen, which costs width but removes a keystroke
 * from every triage pass.
 */
export type ReadingLayout = 'full' | 'right' | 'bottom' | 'top';
export type AccentName =
  | 'iris'
  | 'violet'
  | 'blue'
  | 'cyan'
  | 'teal'
  | 'green'
  | 'amber'
  | 'crimson';

export const THEME_STORAGE_KEY = 'kiln.theme';
const DENSITY_STORAGE_KEY = 'kiln.density';
const DRAWER_PINNED_KEY = 'kiln.drawerPinned';
const APPEARANCE_STORAGE_KEY = 'kiln.appearance';

// --- Appearance ---------------------------------------------------------------

export interface Appearance {
  accent: AccentName;
  rowLines: RowLines;
  measure: Measure;
  fontScale: FontScale;
  showAvatars: boolean;
  showSnippets: boolean;
  showSplitTabs: boolean;
  /** Only consulted when the server reports aiEnabled. */
  showAi: boolean;
  /**
   * Load remote images without asking. Always routed through the server-side
   * proxy, so a sender learns that a message was opened but nothing about the
   * reader — not their IP, cookies, referrer or user agent.
   */
  autoLoadImages: boolean;
  readingLayout: ReadingLayout;
}

export const DEFAULT_APPEARANCE: Appearance = {
  autoLoadImages: true,
  readingLayout: 'full',
  accent: 'iris',
  rowLines: 1,
  measure: 'default',
  fontScale: 100,
  showAvatars: true,
  showSnippets: true,
  showSplitTabs: true,
  showAi: true,
};

export const DEFAULT_THEME: Theme = 'system';
export const DEFAULT_DENSITY: Density = 'cozy';

/**
 * The accent ramp is generated, never hand-picked.
 *
 * Radix's iris scale is the reference: these are its twelve steps read back as
 * OKLCH lightness and chroma. Swapping the hue and keeping the L/C curve gives
 * a scale with the same contrast behaviour at every step, which is the whole
 * point — a hand-written palette per accent would drift within two edits.
 */
const RAMP_LIGHT: readonly [number, number][] = [
  [0.994, 0.004],
  [0.985, 0.01],
  [0.964, 0.021],
  [0.938, 0.035],
  [0.907, 0.048],
  [0.87, 0.062],
  [0.816, 0.081],
  [0.736, 0.114],
  [0.52, 0.207],
  [0.494, 0.203],
  [0.516, 0.176],
  [0.312, 0.117],
];

const RAMP_DARK: readonly [number, number][] = [
  [0.19, 0.024],
  [0.213, 0.041],
  [0.297, 0.093],
  [0.353, 0.14],
  [0.4, 0.14],
  [0.443, 0.132],
  [0.492, 0.126],
  [0.564, 0.14],
  [0.52, 0.207],
  [0.56, 0.19],
  [0.76, 0.13],
  [0.91, 0.06],
];

/**
 * `warm` marks hues that cannot carry white text at the reference lightness.
 * Rather than hand-tune twelve values for amber, steps 9 and 10 — the only two
 * that ever sit behind text — are lifted and the contrast colour is flipped.
 */
export interface AccentSpec {
  name: AccentName;
  label: string;
  hue: number;
  chroma: number;
  warm?: true;
}

export const ACCENTS: readonly AccentSpec[] = [
  { name: 'iris', label: 'Iris', hue: 275, chroma: 1 },
  { name: 'violet', label: 'Violet', hue: 305, chroma: 0.96 },
  { name: 'blue', label: 'Blue', hue: 252, chroma: 1 },
  { name: 'cyan', label: 'Cyan', hue: 215, chroma: 0.88 },
  { name: 'teal', label: 'Teal', hue: 182, chroma: 0.8 },
  { name: 'green', label: 'Green', hue: 148, chroma: 0.82 },
  { name: 'amber', label: 'Amber', hue: 75, chroma: 0.9, warm: true },
  { name: 'crimson', label: 'Crimson', hue: 18, chroma: 0.98 },
];

const ACCENT_BY_NAME = new Map(ACCENTS.map((a) => [a.name, a]));

function accentSpec(name: AccentName): AccentSpec {
  return ACCENT_BY_NAME.get(name) ?? ACCENTS[0]!;
}

/** Step index 0–11 as an `oklch()` string for the given accent and theme. */
function ramp(spec: AccentSpec, step: number, dark: boolean): string {
  const table = dark ? RAMP_DARK : RAMP_LIGHT;
  const entry = table[step] ?? table[table.length - 1]!;
  let [l, c] = entry;
  // 9 and 10 are the solid fills — the only steps that ever sit behind text.
  if (spec.warm && (step === 8 || step === 9)) l = Math.min(0.86, l + 0.28);
  return `oklch(${l.toFixed(3)} ${(c * spec.chroma).toFixed(3)} ${spec.hue})`;
}

/** The swatch colour shown in the picker: step 9, the accent's "true" tone. */
export function accentSwatch(name: AccentName, dark: boolean): string {
  return ramp(accentSpec(name), 8, dark);
}

/** Foreground that stays legible on `accentSwatch` for the same accent. */
export function accentContrast(name: AccentName): string {
  return accentSpec(name).warm ? '#1a1917' : '#ffffff';
}

const MEASURE_PX: Record<Measure, string> = {
  narrow: '640px',
  default: '720px',
  wide: '840px',
  full: '100%',
};

/** The eight mailbox rail hues, as hex because the server stores hex. */
export const ACCOUNT_PALETTE: readonly { hex: string; label: string }[] = [
  { hex: '#3e63dd', label: 'Blue' },
  { hex: '#12a594', label: 'Teal' },
  { hex: '#f76b15', label: 'Orange' },
  { hex: '#e93d82', label: 'Pink' },
  { hex: '#46a758', label: 'Green' },
  { hex: '#6e56cf', label: 'Violet' },
  { hex: '#ffb224', label: 'Amber' },
  { hex: '#00a2c7', label: 'Cyan' },
];

// --- Persistence --------------------------------------------------------------

/** Safari in private mode throws on localStorage; a preference is not worth a
 *  blank screen, so both directions degrade to "use the default". */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* preference simply will not persist */
  }
}

function readTheme(): Theme {
  const stored = readStored(THEME_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : DEFAULT_THEME;
}

function readDensity(): Density {
  const stored = readStored(DENSITY_STORAGE_KEY);
  return stored === 'compact' || stored === 'cozy' || stored === 'comfortable'
    ? stored
    : DEFAULT_DENSITY;
}

const isReadingLayout = (v: unknown): v is ReadingLayout =>
  v === 'full' || v === 'right' || v === 'bottom' || v === 'top';

const isMeasure = (v: unknown): v is Measure =>
  v === 'narrow' || v === 'default' || v === 'wide' || v === 'full';

/** Unknown or corrupt fields fall back per-key, so one bad value cannot wipe the set. */
function readAppearance(): Appearance {
  const raw = readStored(APPEARANCE_STORAGE_KEY);
  if (!raw) return DEFAULT_APPEARANCE;
  let parsed: Record<string, unknown>;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return DEFAULT_APPEARANCE;
    parsed = value as Record<string, unknown>;
  } catch {
    return DEFAULT_APPEARANCE;
  }
  const bool = (key: keyof Appearance, fallback: boolean) =>
    typeof parsed[key] === 'boolean' ? (parsed[key] as boolean) : fallback;
  return {
    accent: ACCENT_BY_NAME.has(parsed.accent as AccentName)
      ? (parsed.accent as AccentName)
      : DEFAULT_APPEARANCE.accent,
    rowLines: parsed.rowLines === 2 ? 2 : 1,
    measure: isMeasure(parsed.measure) ? parsed.measure : DEFAULT_APPEARANCE.measure,
    fontScale:
      parsed.fontScale === 90 || parsed.fontScale === 110
        ? parsed.fontScale
        : DEFAULT_APPEARANCE.fontScale,
    showAvatars: bool('showAvatars', DEFAULT_APPEARANCE.showAvatars),
    showSnippets: bool('showSnippets', DEFAULT_APPEARANCE.showSnippets),
    showSplitTabs: bool('showSplitTabs', DEFAULT_APPEARANCE.showSplitTabs),
    showAi: bool('showAi', DEFAULT_APPEARANCE.showAi),
    autoLoadImages: bool('autoLoadImages', DEFAULT_APPEARANCE.autoLoadImages),
    readingLayout: isReadingLayout(parsed.readingLayout)
      ? parsed.readingLayout
      : DEFAULT_APPEARANCE.readingLayout,
  };
}

// --- Applying to the document -------------------------------------------------

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function isDarkNow(theme: Theme): boolean {
  return theme === 'dark' || (theme === 'system' && prefersDark());
}

/**
 * Inline custom properties on <html> beat every stylesheet rule, including the
 * `[data-theme]` blocks — which is exactly why the ramp has to be re-applied
 * whenever the effective theme changes, not just when the accent does.
 */
function applyAccent(accent: AccentName, theme: Theme): void {
  if (typeof document === 'undefined') return;
  const spec = accentSpec(accent);
  const dark = isDarkNow(theme);
  const style = document.documentElement.style;
  for (let step = 0; step < 12; step += 1) {
    style.setProperty(`--accent-${step + 1}`, ramp(spec, step, dark));
  }
  style.setProperty('--accent-contrast', spec.warm ? '#1a1917' : '#ffffff');
  // The focus ring and link colour are accent-derived, and both are taken from
  // step 11 — the one text-grade step, and the only mid-tone the `warm` lift
  // does not touch. Deriving the ring from step 9 would leave amber with a
  // near-invisible focus indicator in light mode.
  style.setProperty('--border-focus', ramp(spec, 10, dark));
  style.setProperty('--text-link', ramp(spec, 10, dark));
}

function applyAppearance(appearance: Appearance): void {
  if (typeof document === 'undefined') return;
  const style = document.documentElement.style;
  style.setProperty('--ui-scale', String(appearance.fontScale / 100));
  style.setProperty('--reading-column', MEASURE_PX[appearance.measure]);
}

/**
 * Mirrors the choice onto <html> exactly the way the inline boot script in
 * index.html does, so the pre-paint theme and the runtime theme never disagree.
 */
function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  writeStored(THEME_STORAGE_KEY, theme);
}

// --- Composer -----------------------------------------------------------------

export type ComposerMode = 'new' | 'reply' | 'reply-all' | 'forward';

export interface ComposerDraft {
  mode: ComposerMode;
  /** Open detached rather than docked in the bottom-right. */
  popout: boolean;
  /** null means "the composer picks a sensible default account". */
  accountId: number | null;
  to: AddressInput[];
  cc: AddressInput[];
  bcc: AddressInput[];
  subject: string;
  bodyHtml: string;
  /** The parent MessageView.messageId, for threading. */
  inReplyTo: string | null;
  /** Space-joined References chain. */
  references: string | null;
  threadKey: string | null;
}

export interface ComposerState extends ComposerDraft {
  open: boolean;
}

const EMPTY_COMPOSER: ComposerState = {
  open: false,
  mode: 'new',
  popout: false,
  accountId: null,
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  bodyHtml: '',
  inReplyTo: null,
  references: null,
  threadKey: null,
};

// --- Store --------------------------------------------------------------------

export interface UiState {
  /** Which server-side thread view the list is showing. */
  view: ThreadViewName;
  setView: (view: ThreadViewName) => void;

  /** null = All Inboxes. Multi-account scope is what turns the rails on. */
  accountFilter: number | null;
  setAccountFilter: (id: number | null) => void;

  /** null = no category split applied. */
  category: ThreadCategory | null;
  setCategory: (category: ThreadCategory | null) => void;

  selectedThreadKey: string | null;
  setSelectedThreadKey: (key: string | null) => void;

  density: Density;
  setDensity: (density: Density) => void;

  appearance: Appearance;
  setAppearance: <K extends keyof Appearance>(key: K, value: Appearance[K]) => void;
  resetAppearance: () => void;

  /**
   * Published by the thread list so the reading pane can show the full Inbox
   * Zero moment instead of "nothing selected". Not persisted — it is a fact
   * about the current query, not a preference.
   */
  listEmpty: boolean;
  setListEmpty: (empty: boolean) => void;

  composer: ComposerState;
  openComposer: (draft?: Partial<ComposerDraft>) => void;
  closeComposer: () => void;

  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;

  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;

  appearanceOpen: boolean;
  setAppearanceOpen: (open: boolean) => void;

  /** The transient navigation overlay: opened, used, dismissed. */
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;

  /**
   * Pinned, the drawer stops being an overlay and becomes a real column beside
   * the list — no scrim, and navigating no longer dismisses it. Superhuman has
   * no equivalent because it is single-account and its drawer is genuinely
   * transient; with eight mailboxes the account switcher is worth keeping on
   * screen if that is how you work. Persisted, because a layout preference that
   * resets on reload is worse than not offering it.
   */
  drawerPinned: boolean;
  setDrawerPinned: (pinned: boolean) => void;

  focusMode: boolean;
  toggleFocusMode: () => void;

  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const initialTheme = readTheme();
const initialAppearance = readAppearance();
const initialDrawerPinned = readStored(DRAWER_PINNED_KEY) === '1';

// Runs at module load, which is before React's first render — a stored 110%
// scale or a narrow measure must not arrive one paint after the layout does.
applyAppearance(initialAppearance);
applyAccent(initialAppearance.accent, initialTheme);

export const useUiStore = create<UiState>((set, get) => ({
  view: 'inbox',
  setView: (view) =>
    // Changing scope invalidates the selection: the thread almost certainly is
    // not in the new list, and a stale reading pane reads as a bug.
    set({ view, selectedThreadKey: null }),

  accountFilter: null,
  setAccountFilter: (accountFilter) => set({ accountFilter, selectedThreadKey: null }),

  category: null,
  setCategory: (category) => set({ category, selectedThreadKey: null }),

  selectedThreadKey: null,
  setSelectedThreadKey: (selectedThreadKey) => set({ selectedThreadKey }),

  density: readDensity(),
  setDensity: (density) => {
    writeStored(DENSITY_STORAGE_KEY, density);
    set({ density });
  },

  appearance: initialAppearance,
  setAppearance: (key, value) => {
    const appearance = { ...get().appearance, [key]: value };
    writeStored(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
    applyAppearance(appearance);
    if (key === 'accent') applyAccent(appearance.accent, get().theme);
    set({ appearance });
  },
  resetAppearance: () => {
    writeStored(APPEARANCE_STORAGE_KEY, JSON.stringify(DEFAULT_APPEARANCE));
    writeStored(DENSITY_STORAGE_KEY, DEFAULT_DENSITY);
    applyAppearance(DEFAULT_APPEARANCE);
    applyTheme(DEFAULT_THEME);
    applyAccent(DEFAULT_APPEARANCE.accent, DEFAULT_THEME);
    set({ appearance: DEFAULT_APPEARANCE, density: DEFAULT_DENSITY, theme: DEFAULT_THEME });
  },

  listEmpty: false,
  setListEmpty: (listEmpty) => set({ listEmpty }),

  composer: EMPTY_COMPOSER,
  openComposer: (draft) => set({ composer: { ...EMPTY_COMPOSER, ...draft, open: true } }),
  closeComposer: () => set({ composer: EMPTY_COMPOSER }),

  paletteOpen: false,
  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),

  settingsOpen: false,
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),

  appearanceOpen: false,
  setAppearanceOpen: (appearanceOpen) => set({ appearanceOpen }),

  drawerOpen: false,
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),

  drawerPinned: initialDrawerPinned,
  setDrawerPinned: (drawerPinned) => {
    writeStored(DRAWER_PINNED_KEY, drawerPinned ? '1' : '0');
    // Unpinning leaves the panel on screen as an overlay rather than vanishing
    // mid-interaction; pinning closes the overlay because the column replaces it.
    set({ drawerPinned, drawerOpen: !drawerPinned });
  },

  focusMode: false,
  toggleFocusMode: () => set({ focusMode: !get().focusMode }),

  theme: initialTheme,
  setTheme: (theme) => {
    applyTheme(theme);
    applyAccent(get().appearance.accent, theme);
    set({ theme });
  },
}));

/**
 * Re-derives the accent ramp when the OS flips light/dark under `system`.
 * Without this the accent keeps the previous theme's lightness curve and the
 * Send button quietly loses its contrast.
 */
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', () => {
    const { theme, appearance } = useUiStore.getState();
    if (theme === 'system') applyAccent(appearance.accent, theme);
  });
}

/**
 * Row height in px for a (density, lines) pair. The list virtualiser needs a
 * number, not a custom property, so this is the single source of truth and the
 * stylesheet deliberately has no twin of it.
 *
 * The one-line numbers are not taste: 36px is the row pitch measured off
 * Superhuman's own help-centre screenshots at 2x, against ~14px text. Cozy is
 * that measurement; compact and comfortable step around it.
 *
 * Two-line rows are the narrow-window fallback only — see ONE_LINE_MIN_PX in
 * ThreadList — so they are sized to hold two 13px lines and nothing more.
 */
export const ROW_HEIGHTS: Record<RowLines, Record<Density, number>> = {
  1: { compact: 30, cozy: 36, comfortable: 44 },
  2: { compact: 44, cozy: 52, comfortable: 60 },
};

/** Kept as the documented name from the shared-modules contract. */
export const useStore = useUiStore;
